import { Subject, Observable } from 'rxjs';
import { ITicketingSystemFilter } from 'src/modules/admin/modules/TicketingSystem/TicketingSystemContext/TicketSystemReducer/TicketSystemReducer';
type PAYMENT_DATA = {
  type: 'RELOAD_PAYMENTS';
  data: null;
};
const subject = new Subject<PAYMENT_DATA>();
/**
 * @description Default event type structure that different events should use
 * action - the type of action event is emitting
 * data   - data associated with the action in emitted event
 */
interface DefaultEventType<A, D> {
  action: A;
  data: D;
}

/**
 * @description Specify here all the event specific type, they should be wrapped in DefaultEventType
 */
declare type EventActionAppVersionUpdated = DefaultEventType<
  'APP_VERSION_UPDATED',
  null
>;
declare type EventActionGuarantorPaymentCompleted = DefaultEventType<
  'GUARANTOR_PAYMENT_COMPLETED',
  null
>;

declare type EventActionPatientCopayAdded = DefaultEventType<
  'PATIENT_COPAY_ADDED',
  null
>;

declare type EventActionSettleGuarantorStatement = DefaultEventType<
  'GUARANTOR_STATEMENT_SETTLED',
  null
>;

declare type EventActionRefreshAppointment = DefaultEventType<
  'REFRESH_APPOINTMENT',
  {
    clinicGroupId: string | number;
    clinicId: string | number;
    appointmentId: string | number;
  }
>;

declare type EventActionPatientCopayRemoved = DefaultEventType<
  'PATIENT_COPAY_REMOVED',
  null
>;

declare type EventActionUpdateCCMGrid = DefaultEventType<
  'CCM_PLAN_STATUS_UPDATED',
  null
>;

declare type EventActionCCMPlanCreated = DefaultEventType<
  'CCM_PLAN_CREATED',
  null | {
    ccmPatientId: number;
  }
>;

declare type EventActionCCMPlanDeleted = DefaultEventType<
  'CCM_PLAN_DELETED',
  null | {
    ccmPatientId: number;
  }
>;

declare type EventActionCCMTaskPlanDetailsUpdated = DefaultEventType<
  'CCM_PLAN_TASK_DETAILS_UPDATED',
  null | {
    ccmPatientId: number;
  }
>;

declare type EventActionCCMPanelDataUpdated = DefaultEventType<
  'CCM_PANEL_DATA_UPDATED',
  null
>;
declare type EventActionRefreshCCMPatientsData = DefaultEventType<
  'CCM_PATIENTS_DATA',
  null
>;

declare type EventActionCCMTime = DefaultEventType<'ADDED_CCM_TIME', null>;

declare type EventActionStoppedCCMTimer = DefaultEventType<
  'STOPPED_CCM_TIMER',
  null
>;

declare type EventActionNavigateToAppointmentTab = DefaultEventType<
  'NAVIGATE_TO_APPOINTMENT_TAB',
  any
>;

declare type EventActionMarkedPreviousFollowUpNotesAsDone = DefaultEventType<
  'MARK_PREVIOUS_FOLLOW_UP_NOTES_AS_DONE',
  {
    patientId?: number;
    clinicId?: number;
  }
>;

declare type EventActionStartTimerFromReminderToaster = DefaultEventType<
  'START_TIMER_FROM_PATIENT_DRAWER_REMINDER',
  null
>;

declare type EventActionTicketTimerPanelDataUpdated = DefaultEventType<
  'TICKET_TIMER_PANEL_DATA_UPDATED',
  null
>;

declare type EventRefreshTicketingSystmGridTable = DefaultEventType<
  'REFRESH_TICKETING_GRID',
  {
    pageNumber?: number;
  } | null
>;

declare type EventUpdateFilterInSystemGridTable = DefaultEventType<
  'UPDATE_FILTERS',
  Record<string, ITicketingSystemFilter>
>;
declare type EventResetFilters = DefaultEventType<'RESET_FILTERS', null>;
declare type EventRefreshDashboard = DefaultEventType<
  'RESET_DASHBOARD',
  {
    clinicId: string | number;
  }
>;
declare type EventResetAdvancedFilters = DefaultEventType<
  'RESET_ADVANCED_FILTERS',
  null
>;
declare type EventApplyAdvancedFilters = DefaultEventType<
  'APPLY_ADVANCED_FILTERS',
  null
>;

declare type EventActionScheduledJobAdded = DefaultEventType<
  'SCHEDULED_JOB_ADDED',
  null
>;
declare type EventActionScheduledJobUpdated = DefaultEventType<
  'SCHEDULED_JOB_UPDATED',
  null
>;
declare type EventActionRefreshPatientsFormData = DefaultEventType<
  'REFRESH_PATIENT_FORM_GRID',
  null
>;

declare type EventConfiguerCategoriesForPractice = DefaultEventType<
  'CONFIGURE_CATEGORIES_FOR_PRACTICE',
  null
>;

declare type EventUpdateIncomingSmsCount = DefaultEventType<
  'EVENT_UPDATE_INCOMING_SMS_COUNT',
  null
>;

declare type EventDeleteCustomInbox = DefaultEventType<
  'EVENT_DELETE_CUSTOM_INBOX',
  null
>;

declare type EventMessagesGridRefresh = DefaultEventType<
  'EVENT_MESSAGES_GRID_REFRESH',
  null
>;
declare type EventUpdateTicketStatus = DefaultEventType<
  'UPDATE_TICKET_STATUS_COUNT',
  null
>;

declare type GetAllServiceLocations = DefaultEventType<
  'GET_ALL_SERVICE_LOCATIONS',
  null
>;

/**
 * @description Event Type will have different events types to emit the event.
 * To specify more types to it, use or operator. For e.g, EventActionAppVersionUpdated | EventActionLogout
 */
declare type EventType =
  | EventActionAppVersionUpdated
  | EventActionGuarantorPaymentCompleted
  | EventActionPatientCopayAdded
  | EventActionSettleGuarantorStatement
  | EventActionPatientCopayRemoved
  | EventActionUpdateCCMGrid
  | EventActionCCMPlanCreated
  | EventActionCCMTaskPlanDetailsUpdated
  | EventActionCCMPlanDeleted
  | EventActionCCMPanelDataUpdated
  | EventActionRefreshCCMPatientsData
  | EventActionCCMTime
  | EventActionStoppedCCMTimer
  | EventActionRefreshAppointment
  | EventActionNavigateToAppointmentTab
  | EventActionMarkedPreviousFollowUpNotesAsDone
  | EventActionStartTimerFromReminderToaster
  | EventActionTicketTimerPanelDataUpdated
  | EventRefreshTicketingSystmGridTable
  | EventUpdateFilterInSystemGridTable
  | EventResetFilters
  | EventRefreshDashboard
  | EventResetAdvancedFilters
  | EventApplyAdvancedFilters
  | EventActionScheduledJobAdded
  | EventActionScheduledJobUpdated
  | EventActionRefreshPatientsFormData
  | EventConfiguerCategoriesForPractice
  | EventUpdateIncomingSmsCount
  | EventDeleteCustomInbox
  | EventMessagesGridRefresh
  | EventUpdateTicketStatus
  | GetAllServiceLocations;

const event = new Subject<EventType>();

export const CommunicationService = {
  getMessage: (): Observable<PAYMENT_DATA> => subject.asObservable(),
  sendMessage: (data: PAYMENT_DATA): void => subject.next(data),
  clearMessages: (): void =>
    subject.next({ type: 'RELOAD_PAYMENTS', data: null }),

  /**
   * @description This is generic event subscription method to capture the event.
   * Use this in the component based on a ref and cancel the subscription if the component is unmounted
   */
  getEvent: (): Observable<EventType> => event.asObservable(),

  /**
   * @description This is generic event emitter method to emit an event.
   * Use this in the component where you want to emit event
   */
  sendEvent: (data: EventType): void => event.next(data)
};
